/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { ListItemContext } from "cms/fields/field-lists/field-list-context";
import dayjs from "dayjs";
import useOrganization from "hooks/useOrganization";
import { useContext, useState } from "react";
import { useDebounce, useFirstMountState } from "react-use";

/**
 * @param {string} dateFormat 
 */
const useOnChangeDateTime = (dateFormat) => {
    const {
        index,
        value,
        settings,
        updateListItem,
    } = useContext(ListItemContext);

    const organization = useOrganization() || {};
    const { tz = organization.tz } = value || {};

    const [fieldValue, setFieldValue] = useState(() => {

        /** @type {TimeFieldValue} */
        const initialValue = value ?
            value : {
                date: "",
                tz
            };

        return {
            value: initialValue,
            settings
        };
    });

    const isFirstMount = useFirstMountState();

    useDebounce(() => {
        // prevent save on initial load
        if (!isFirstMount) {
            updateListItem(fieldValue, index);
        }
    }, 600, [fieldValue]);

    const onSettingsChange = (updateState) => {
        setFieldValue(updateState);
    };

    /**
     * @param {Date | null} selectedDate 
     */
    const onChangeDateTime = (selectedDate) => {
        const selected = selectedDate 
            ? dayjs(selectedDate).format(dateFormat)
            : "";

        const newValue = {
            date: selected,
            tz,
        };

        setFieldValue((prev) => {
            return {
                ...prev,
                value: newValue
            };
        });
    };

    return {
        dateTimeString: fieldValue?.value?.date,
        fieldValue,
        onChangeDateTime,
        onSettingsChange,
        setFieldValue,
    };
};

export { useOnChangeDateTime };


/**
 * @typedef {object} TimeFieldValue
 * @property {string} date
 * @property {string} tz
 */
